import { useEffect, useState } from 'react'
import { adminAPI } from '../services/api'
import { apiErrorMessage } from '../utils/apiError'
import type { BreakerRule, BreakerRuleName, EnginePauseReport, EngineSwitchState } from '../types/enginePause'

/**
 * Admin diagnostics: the engine pause (backend services/engine_switches and
 * services/engine_breaker). Whether the engine is publishing, the breaker's
 * rules over the current record, what a pause has held back, and a reasoned
 * pause or release by hand. While paused the scan keeps running and setups
 * that clear every gate are shadow-logged instead of published.
 */

const RULES: Record<BreakerRuleName, string> = {
  streak: 'Losses in a row',
  net_r: 'Net R',
  win_rate: 'Win rate',
}

const signedR = (v: number | null) => (v === null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}R`)

export const ruleValue = (name: BreakerRuleName, v: number | null) =>
  v === null ? '—' : name === 'net_r' ? signedR(v) : name === 'win_rate' ? `${v.toFixed(1)}%` : String(v)

const ruleLimit = (name: BreakerRuleName, t: number) =>
  name === 'streak' ? `${t} or more` : `${name === 'net_r' ? signedR(t) : `${t}%`} or below`

const when = (iso: string | null) => (iso ? iso.replace('T', ' ').slice(0, 16) + ' UTC' : '—')

export const who = (setBy: string) => (setBy.startsWith('auto:') ? 'the engine breaker' : setBy.replace(/^admin:/, ''))

const RuleRow = ({ name, rule, minimum }: { name: BreakerRuleName; rule: BreakerRule; minimum: number }) => (
  <tr className="border-t border-gray-100" data-testid={`breaker-rule-${name}`}>
    <td className="px-2 py-1.5 text-gray-800">{RULES[name]}</td>
    <td className="px-2 py-1.5 tabular-nums">{ruleValue(name, rule.value)}</td>
    <td className="px-2 py-1.5 tabular-nums text-gray-500">{ruleLimit(name, rule.threshold)}</td>
    <td className="px-2 py-1.5 text-xs">
      {!rule.measured ? (
        <span className="text-gray-500">needs {minimum} closes</span>
      ) : rule.tripped ? (
        <span className="font-medium text-red-700">tripped</span>
      ) : (
        <span className="text-green-700">ok</span>
      )}
    </td>
  </tr>
)

const Change = ({ c }: { c: EngineSwitchState }) => (
  <li className="text-xs text-gray-600" data-testid={`engine-switch-${c.id}`}>
    {when(c.set_at)} · <span className={c.paused ? 'text-red-700' : 'text-green-700'}>{c.paused ? 'paused' : 'released'}</span>{' '}
    by {who(c.set_by)}: {c.reason}
  </li>
)

const EnginePausePanel = () => {
  const [data, setData] = useState<EnginePauseReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    adminAPI
      .enginePause()
      .then((d) => live && setData(d))
      .catch((e) => live && setError(apiErrorMessage(e, 'Could not load the engine pause')))
    return () => {
      live = false
    }
  }, [])

  const paused = data?.pause ?? null
  const auto = paused?.set_by.startsWith('auto:') ?? false

  const submit = () => {
    if (!data) return
    setBusy(true)
    setError(null)
    adminAPI
      .setEnginePause(!paused, reason.trim())
      .then((d) => {
        setData(d)
        setReason('')
      })
      .catch((e) => setError(apiErrorMessage(e, 'Could not change the engine pause')))
      .finally(() => setBusy(false))
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4" aria-label="Engine pause" data-testid="engine-pause">
      <h2 className="text-lg font-semibold text-gray-900">Engine pause</h2>
      {data && (
        <>
          {paused ? (
            <p className="mt-2 rounded bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="engine-pause-status">
              <span className="font-semibold">Paused</span> since {when(paused.set_at)} by {who(paused.set_by)}:{' '}
              {paused.reason}. The scan keeps running; setups that clear every gate are shadow-logged, not published.
            </p>
          ) : (
            <p className="mt-2 text-sm text-gray-800" data-testid="engine-pause-status">
              <span className="font-semibold text-green-700">Publishing.</span> The engine is not paused.
            </p>
          )}

          {paused && data.held_back && (
            <p className="mt-2 text-sm text-gray-700" data-testid="engine-held-back">
              Held back so far: {data.held_back.logged} setups, {data.held_back.resolved} resolved, net{' '}
              {signedR(data.held_back.net_r)}.{' '}
              {auto
                ? `Lifts on its own once at least ${data.release_min_resolved} have resolved with net R above zero.`
                : 'Paused by an admin: an admin releases it.'}
            </p>
          )}

          <h3 className="mt-4 text-sm font-semibold text-gray-800">
            Automatic breaker: {data.enabled ? 'on' : 'off'}
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            {data.enabled
              ? 'Checked hourly. Any tripped rule pauses the engine.'
              : 'ENGINE_BREAKER_ENABLED is off: the rules are shown, nothing pauses automatically. Run scripts/replay_engine_breaker.py on the real record before turning it on.'}{' '}
            Engine record closes in the last {data.assessment.lookback_days} days
            {data.released_at ? `, opened after the last release (${when(data.released_at)})` : ''}:{' '}
            {data.assessment.closes}.
          </p>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className="px-2 py-1 text-left font-medium">Rule</th>
                  <th className="px-2 py-1 text-left font-medium">Now</th>
                  <th className="px-2 py-1 text-left font-medium">Pauses at</th>
                  <th className="px-2 py-1 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {(Object.keys(RULES) as BreakerRuleName[]).map((name) => (
                  <RuleRow key={name} name={name} rule={data.assessment.rules[name]} minimum={data.assessment.minimum_closes} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <label className="sr-only" htmlFor="engine-pause-reason">
              Reason
            </label>
            <input
              id="engine-pause-reason"
              className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
              placeholder={paused ? 'Why release it now?' : 'Why pause it?'}
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
            <button
              type="button"
              className={`rounded px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 ${
                paused ? 'bg-green-700 hover:bg-green-800' : 'bg-red-700 hover:bg-red-800'
              }`}
              disabled={busy || reason.trim().length < 3}
              onClick={submit}
            >
              {paused ? 'Release engine' : 'Pause engine'}
            </button>
          </div>
          {!paused && (
            <p className="mt-1 text-xs text-gray-500" data-testid="pause-reason-public">
              The reason is shown publicly with the pause on the Track Record and fee calculator, without your name.
            </p>
          )}

          {data.history.length > 0 && (
            <>
              <h3 className="mt-4 text-sm font-semibold text-gray-800">Changes</h3>
              <ul className="mt-1 space-y-1">
                {data.history.map((c) => (
                  <Change key={c.id} c={c} />
                ))}
              </ul>
            </>
          )}
        </>
      )}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </section>
  )
}

export default EnginePausePanel
